import { ArrowDown, ArrowUp, Gauge, Minus, MoveHorizontal } from 'lucide-react';
import type { ReactNode } from 'react';
import type { AlertDetailResponse } from '@/types/alerts';
import {
  EvidenceRow,
  HeadlineLine,
  MissingContextLine,
  RendererShell,
  formatDateTime,
  formatInt,
  getNum,
  getStr,
} from './_shared';

/**
 * OD-001 / OD-002 / OD-003 / OD-005 / OD-006 — Odometer family.
 *
 * Branched single renderer. Each sub-rule has a distinct evidence shape
 * verified from live fraud.alert + domain/fraud_rules/odometer_rules.py:
 *
 *   OD-001 Rollback: current_odometer, previous_odometer, rollback_km,
 *                    days_between, previous_transaction_datetime
 *   OD-002 Stagnant: odometer_reading, occurrence_count,
 *                    transactions_with_same_reading[]
 *   OD-003 Jump:     current_odometer, previous_odometer, distance_km,
 *                    time_hours, implied_speed_kmh, max_expected_km,
 *                    days_elapsed
 *   OD-005 Zero:     odometer_value, fuel_volume, total_amount
 *   OD-006 Seq Brk:  current_odometer, previous_odometer,
 *                    expected_max_odometer, gap_km, days_between,
 *                    max_daily_distance_km
 */
export default function OdometerFamilyRenderer(
  evidence: Record<string, unknown> | null | undefined,
  alert: AlertDetailResponse,
): JSX.Element {
  switch (alert.rule_id) {
    case 'OD-001':
      return <RollbackBranch evidence={evidence} alert={alert} />;
    case 'OD-002':
      return <StagnantBranch evidence={evidence} alert={alert} />;
    case 'OD-003':
      return <JumpBranch evidence={evidence} alert={alert} />;
    case 'OD-005':
      return <ZeroBranch evidence={evidence} alert={alert} />;
    case 'OD-006':
      return <SequenceBreakBranch evidence={evidence} alert={alert} />;
    default:
      return (
        <RendererShell>
          <MissingContextLine
            message={`No specialised view for ${alert.rule_id} — see raw evidence below.`}
          />
        </RendererShell>
      );
  }
}

function VehicleHeader({
  alert,
  evidence,
}: {
  alert: AlertDetailResponse;
  evidence: Record<string, unknown> | null | undefined;
}): ReactNode {
  const vehicleReg =
    getStr(evidence, 'vehicle_registration') ?? alert.asset_token ?? null;
  if (!vehicleReg) return null;
  return (
    <span className="font-mono text-xs text-muted-foreground">
      {vehicleReg}
    </span>
  );
}

function RollbackBranch({
  evidence,
  alert,
}: {
  evidence: Record<string, unknown> | null | undefined;
  alert: AlertDetailResponse;
}): JSX.Element {
  const current = getNum(evidence, 'current_odometer');
  const previous = getNum(evidence, 'previous_odometer');
  const rollback = getNum(evidence, 'rollback_km');
  const days = getNum(evidence, 'days_between');
  const prevDt = getStr(evidence, 'previous_transaction_datetime');

  return (
    <RendererShell>
      <div className="flex items-start gap-2">
        <ArrowDown className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-600" />
        <HeadlineLine>
          Odometer rolled <span className="text-red-700">backwards</span> by{' '}
          {formatInt(rollback)} km
          {days !== undefined ? ` over ${days.toFixed(1)} days` : ''} — readings
          should only ever increase.
        </HeadlineLine>
      </div>

      <div className="flex items-center justify-center gap-3 rounded-md border bg-muted/20 px-3 py-3 text-sm tabular-nums">
        <span className="font-bold">{formatInt(previous)} km</span>
        <ArrowDown className="h-4 w-4 text-red-600" />
        <span className="font-bold">{formatInt(current)} km</span>
        <span className="ml-2 rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800">
          −{formatInt(rollback)} km
        </span>
      </div>

      <div className="rounded-md border bg-muted/20 px-3 py-2">
        <EvidenceRow label="Vehicle">
          <VehicleHeader alert={alert} evidence={evidence} />
        </EvidenceRow>
        <EvidenceRow label="Previous reading">
          {formatInt(previous)} km
        </EvidenceRow>
        <EvidenceRow label="Previous reading at">
          {formatDateTime(prevDt)}
        </EvidenceRow>
        <EvidenceRow label="Current reading">
          {formatInt(current)} km
        </EvidenceRow>
        <EvidenceRow label="Days between">
          {days !== undefined ? days.toFixed(1) : '—'}
        </EvidenceRow>
      </div>
    </RendererShell>
  );
}

function StagnantBranch({
  evidence,
  alert,
}: {
  evidence: Record<string, unknown> | null | undefined;
  alert: AlertDetailResponse;
}): JSX.Element {
  const reading = getNum(evidence, 'odometer_reading');
  const count = getNum(evidence, 'occurrence_count');

  return (
    <RendererShell>
      <div className="flex items-start gap-2">
        <Minus className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
        <HeadlineLine>
          Same odometer reading of {formatInt(reading)} km has been recorded{' '}
          <span className="text-red-700">{count ?? '—'} times</span> for this
          vehicle — possible odometer disconnect or driver entering a fixed
          value.
        </HeadlineLine>
      </div>

      <div className="rounded-md border bg-muted/20 px-3 py-2">
        <EvidenceRow label="Vehicle">
          <VehicleHeader alert={alert} evidence={evidence} />
        </EvidenceRow>
        <EvidenceRow label="Stagnant reading">
          {formatInt(reading)} km
        </EvidenceRow>
        <EvidenceRow label="Occurrence count">{count ?? '—'}</EvidenceRow>
      </div>
    </RendererShell>
  );
}

function JumpBranch({
  evidence,
  alert,
}: {
  evidence: Record<string, unknown> | null | undefined;
  alert: AlertDetailResponse;
}): JSX.Element {
  const distance = getNum(evidence, 'distance_km');
  const hours = getNum(evidence, 'time_hours');
  const speed = getNum(evidence, 'implied_speed_kmh');
  const maxExpected = getNum(evidence, 'max_expected_km');
  const days = getNum(evidence, 'days_elapsed');
  const kmPerDay =
    distance !== undefined && days !== undefined && days > 0
      ? distance / days
      : undefined;

  return (
    <RendererShell>
      <div className="flex items-start gap-2">
        <ArrowUp className="mt-0.5 h-4 w-4 flex-shrink-0 text-orange-600" />
        <HeadlineLine>
          Odometer jumped {formatInt(distance)} km in{' '}
          {hours !== undefined ? `${hours.toFixed(1)}h` : 'unknown time'}
          {speed !== undefined
            ? ` — implied average speed ${speed.toFixed(0)} km/h`
            : ''}
          {kmPerDay !== undefined
            ? `, ${kmPerDay.toFixed(0)} km/day`
            : ''}
          .
        </HeadlineLine>
      </div>

      <div className="rounded-md border bg-muted/20 px-3 py-2">
        <EvidenceRow label="Vehicle">
          <VehicleHeader alert={alert} evidence={evidence} />
        </EvidenceRow>
        <EvidenceRow label="Distance">{formatInt(distance)} km</EvidenceRow>
        <EvidenceRow label="Elapsed time">
          {hours !== undefined ? `${hours.toFixed(1)} h` : '—'}
        </EvidenceRow>
        <EvidenceRow label="Implied avg speed">
          {speed !== undefined ? `${speed.toFixed(0)} km/h` : '—'}
        </EvidenceRow>
        <EvidenceRow label="Max expected for window">
          {formatInt(maxExpected)} km
        </EvidenceRow>
      </div>
    </RendererShell>
  );
}

function ZeroBranch({
  evidence,
  alert,
}: {
  evidence: Record<string, unknown> | null | undefined;
  alert: AlertDetailResponse;
}): JSX.Element {
  const value = getNum(evidence, 'odometer_value');
  const fuelVolume = getNum(evidence, 'fuel_volume');
  const totalAmount = getNum(evidence, 'total_amount');

  return (
    <RendererShell>
      <div className="flex items-start gap-2">
        <Gauge className="mt-0.5 h-4 w-4 flex-shrink-0 text-blue-600" />
        <HeadlineLine>
          Transaction submitted with{' '}
          <span className="text-red-700">
            zero or missing odometer reading
          </span>{' '}
          — distance-based fraud rules cannot evaluate this fill.
        </HeadlineLine>
      </div>

      <div className="rounded-md border bg-muted/20 px-3 py-2">
        <EvidenceRow label="Vehicle">
          <VehicleHeader alert={alert} evidence={evidence} />
        </EvidenceRow>
        <EvidenceRow label="Odometer value">
          {value !== undefined ? formatInt(value) : '—'}
        </EvidenceRow>
        <EvidenceRow label="Fuel volume">
          {fuelVolume !== undefined ? `${fuelVolume.toFixed(2)} L` : '—'}
        </EvidenceRow>
        <EvidenceRow label="Transaction total">
          {totalAmount !== undefined
            ? `R${totalAmount.toFixed(2)}`
            : '—'}
        </EvidenceRow>
      </div>
    </RendererShell>
  );
}

function SequenceBreakBranch({
  evidence,
  alert,
}: {
  evidence: Record<string, unknown> | null | undefined;
  alert: AlertDetailResponse;
}): JSX.Element {
  const current = getNum(evidence, 'current_odometer');
  const previous = getNum(evidence, 'previous_odometer');
  const expectedMax = getNum(evidence, 'expected_max_odometer');
  const gap = getNum(evidence, 'gap_km');
  const days = getNum(evidence, 'days_between');
  const maxDaily = getNum(evidence, 'max_daily_distance_km');

  return (
    <RendererShell>
      <div className="flex items-start gap-2">
        <MoveHorizontal className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
        <HeadlineLine>
          Unexplained gap of <span className="text-red-700">{formatInt(gap)} km</span>{' '}
          beyond the maximum expected reading
          {days !== undefined ? ` over ${days.toFixed(1)} days` : ''} — suggests
          unreported vehicle use or odometer manipulation.
        </HeadlineLine>
      </div>

      <div className="rounded-md border bg-muted/20 px-3 py-2">
        <EvidenceRow label="Vehicle">
          <VehicleHeader alert={alert} evidence={evidence} />
        </EvidenceRow>
        <EvidenceRow label="Previous reading">
          {formatInt(previous)} km
        </EvidenceRow>
        <EvidenceRow label="Expected max">
          {formatInt(expectedMax)} km
        </EvidenceRow>
        <EvidenceRow label="Current reading">
          {formatInt(current)} km
        </EvidenceRow>
        <EvidenceRow label="Unexplained gap">
          <span className="font-medium text-red-700">
            +{formatInt(gap)} km
          </span>
        </EvidenceRow>
        <EvidenceRow label="Max realistic km/day">
          {formatInt(maxDaily)}
        </EvidenceRow>
      </div>
    </RendererShell>
  );
}
